import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AdminCreateLeaveRequestData,
  CreateLeaveRequestData,
  DecideLeaveRequestData,
  LeaveRequestFilters,
  leaveRequestsApi,
} from '../leave-requests';

export const leaveRequestKeys = {
  all: ['leave-requests'] as const,
  mine: () => [...leaveRequestKeys.all, 'mine'] as const,
  mineDetail: (id: string) => [...leaveRequestKeys.all, 'mine', 'detail', id] as const,
  myEntitlement: (year?: string) => [...leaveRequestKeys.all, 'mine', 'entitlement', year ?? null] as const,
  staffAbsences: () => [...leaveRequestKeys.all, 'staff-absences'] as const,
  adminList: (filters: LeaveRequestFilters = {}) => [...leaveRequestKeys.all, 'admin', 'list', filters] as const,
  adminDetail: (id: string) => [...leaveRequestKeys.all, 'admin', 'detail', id] as const,
  adminByUser: (userId: string, year?: string) =>
    [...leaveRequestKeys.all, 'admin', 'by-user', userId, year ?? null] as const,
};

export function useMyLeaveRequestsQuery() {
  return useQuery({
    queryKey: leaveRequestKeys.mine(),
    queryFn: () => leaveRequestsApi.getMine(),
  });
}

export function useMyLeaveRequestQuery(id?: string) {
  return useQuery({
    queryKey: id ? leaveRequestKeys.mineDetail(id) : [...leaveRequestKeys.all, 'mine', 'detail', null],
    queryFn: () => leaveRequestsApi.mineDetail(id as string),
    enabled: Boolean(id),
  });
}

/** Every staff member's view of who's out — admin, teacher and employee alike, unlike the rest
 *  of this file which is either "my own" or admin-only. */
export function useStaffAbsencesQuery() {
  return useQuery({
    queryKey: leaveRequestKeys.staffAbsences(),
    queryFn: () => leaveRequestsApi.getStaffAbsences(),
  });
}

export function useMyLeaveEntitlementQuery(year?: string) {
  return useQuery({
    queryKey: leaveRequestKeys.myEntitlement(year),
    queryFn: () => leaveRequestsApi.getMyEntitlement(year),
  });
}

export function useCreateLeaveRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLeaveRequestData) => leaveRequestsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveRequestKeys.all });
    },
  });
}

export function useAdminCreateLeaveRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminCreateLeaveRequestData) => leaveRequestsApi.adminCreate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveRequestKeys.all });
    },
  });
}

export function useUpdateMyLeaveRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateLeaveRequestData }) => leaveRequestsApi.updateMine(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveRequestKeys.all });
    },
  });
}

export function useAdminUpdateLeaveRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateLeaveRequestData }) => leaveRequestsApi.adminUpdate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveRequestKeys.all });
    },
  });
}

export function useAdminLeaveRequestsListQuery(filters: LeaveRequestFilters = {}) {
  return useQuery({
    queryKey: leaveRequestKeys.adminList(filters),
    queryFn: () => leaveRequestsApi.adminList(filters),
  });
}

export function useAdminLeaveRequestQuery(id?: string) {
  return useQuery({
    queryKey: id ? leaveRequestKeys.adminDetail(id) : [...leaveRequestKeys.all, 'admin', 'detail', null],
    queryFn: () => leaveRequestsApi.adminGet(id as string),
    enabled: Boolean(id),
  });
}

export function useAdminLeaveRequestsByUserQuery(userId?: string, year?: string) {
  return useQuery({
    queryKey: userId
      ? leaveRequestKeys.adminByUser(userId, year)
      : [...leaveRequestKeys.all, 'admin', 'by-user', null, year ?? null],
    queryFn: () => leaveRequestsApi.adminGetByUser(userId as string, year),
    enabled: Boolean(userId),
  });
}

export function useDecideLeaveRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DecideLeaveRequestData }) => leaveRequestsApi.adminDecide(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveRequestKeys.all });
    },
  });
}

export function useAddLeaveRequestNoteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) => leaveRequestsApi.adminAddNote(id, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: leaveRequestKeys.adminDetail(variables.id) });
    },
  });
}

export function useDeleteLeaveRequestNoteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, noteId }: { id: string; noteId: string }) => leaveRequestsApi.adminDeleteNote(id, noteId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: leaveRequestKeys.adminDetail(variables.id) });
    },
  });
}
